import { databases } from '@/lib/appwrite';
import { Query } from 'appwrite';
import type { Models } from 'appwrite';


const DB_ID = import.meta.env.VITE_APPWRITE_DATABASE_ID;
const VISITS_COLLECTION_ID = import.meta.env.VITE_VISITS_COLLECTION_ID;
const ORGS_COLLECTION_ID = import.meta.env.VITE_ORGANIZATIONS_COLLECTION_ID;
const VISIT_LOGS_COLLECTION_ID = import.meta.env.VITE_VISIT_LOGS_COLLECTION_ID;

interface VisitDocument extends Models.Document {
    userId: string;
    organizationId: string;
    status: string;
    visitDates: string[];
    visitPurpose?: string | null;
    userComment?: string | null;
    contactPerson?: string | null;
    contactRole?: string | null;
    agreements?: string[];
    observations?: string[];
    signature?: string[];
    branch?: string | null;
    usersId?: string[] | null;

}

interface OrganizationDocument extends Models.Document {
    name: string;
    city?: string;
    address?: string;
}

interface VisitLogDocument extends Models.Document {
    visitId: string;
    contactPerson?: string | null;
    contactRole?: string | null;
    agreements?: string[];
    observations?: string[];
    signature?: string[];
}

interface VisitWithOrgName extends VisitDocument {
    organizationName: string;
    organizationCity?: string;
    organizationAddress?: string;
    usersId?: string[] | null;

}

interface GetVisitsParams {
    userId?: string;
    month?: string;
    searchQuery?: string;
    statusFilter?: string;
}

interface StatsResponse {
    totalRecords: number;
    totalUsers: number;
    pending: number;
    completed: number;
    canceled: number;
}

interface UserStatsResponse {
    totalRecords: number;
    pending: number;
    completed: number;
    canceled: number;
}

// Helper para dividir arrays en chunks
const chunkArray = <T>(arr: T[], size: number): T[][] =>
    Array.from({ length: Math.ceil(arr.length / size) }, (_, i) =>
        arr.slice(i * size, i * size + size)
    );

// Helper para query en chunks y flatten
const listByIds = async <T extends Models.Document>(
    dbId: string,
    collectionId: string,
    field: string,
    ids: string[],
    selectFields: string[]
): Promise<T[]> => {
    if (ids.length === 0) return [];

    const chunks = chunkArray(ids, 100);
    const results = await Promise.all(
        chunks.map(chunk =>
            databases.listDocuments<T>(dbId, collectionId, [
                Query.equal(field, chunk),
                Query.select(selectFields),
                Query.limit(chunk.length)
            ])
        )
    );
    return results.flatMap(r => r.documents);
};

export const getAllVisits = async (
    params: GetVisitsParams,
    limit: number = 10,
    offset: number = 0
): Promise<{ documents: VisitWithOrgName[]; total: number }> => {
    try {
        const { userId, month, searchQuery, statusFilter } = params;

        const fetchLimit = (month || searchQuery) ? 500 : limit;

        const queries: string[] = [
            Query.select(['$id', 'userId', 'organizationId', 'status', 'visitDates', 'visitPurpose', 'userComment', 'branch', '$createdAt', 'usersId']),
            Query.limit(fetchLimit),
            Query.orderDesc('$createdAt')
        ];

        if (userId && userId !== 'all') {
            queries.push(Query.equal('userId', userId));
        }

        if (statusFilter && statusFilter !== 'all') {
            queries.push(Query.equal('status', statusFilter));
        }

        if (!searchQuery && !month) {
            queries.push(Query.offset(offset));
        }

        const visitsResponse = await databases.listDocuments<VisitDocument>(
            DB_ID,
            VISITS_COLLECTION_ID,
            queries
        );

        const orgIds = [...new Set(visitsResponse.documents.map(v => v.organizationId))];
        const visitIds = visitsResponse.documents.map(v => v.$id);

        const [orgsDocuments, logsDocuments] = await Promise.all([
            listByIds<OrganizationDocument>(
                DB_ID, ORGS_COLLECTION_ID, '$id', orgIds,
                ['$id', 'name', 'city', 'address']
            ),
            listByIds<VisitLogDocument>(
                DB_ID, VISIT_LOGS_COLLECTION_ID, 'visitId', visitIds,
                ['visitId', 'contactPerson', 'contactRole', 'agreements', 'observations', 'signature']
            )
        ]);

        const organizationsMap = new Map<string, OrganizationDocument>();
        orgsDocuments.forEach(org => organizationsMap.set(org.$id, org));

        const logsMap = new Map<string, VisitLogDocument>();
        logsDocuments.forEach(log => logsMap.set(log.visitId, log));

        let visitsWithOrgNames: VisitWithOrgName[] = visitsResponse.documents.map(visit => {
            const org = organizationsMap.get(visit.organizationId);
            const log = logsMap.get(visit.$id);

            return {
                ...visit,
                organizationName: org?.name || 'Sin nombre',
                organizationCity: org?.city,
                organizationAddress: org?.address,
                usersId: visit.usersId || null, 
                contactPerson: log?.contactPerson || null,
                contactRole: log?.contactRole || null,
                agreements: log?.agreements || [],
                observations: log?.observations || [],
                signature: log?.signature || []
            };
        });

        if (month) {
            const [year, monthNum] = month.split('-');
            visitsWithOrgNames = visitsWithOrgNames.filter(visit => {
                if (!visit.visitDates || visit.visitDates.length < 2) return false;
                const visitDate = new Date(visit.visitDates[1]);
                const visitYear = visitDate.getFullYear().toString();
                const visitMonth = String(visitDate.getMonth() + 1).padStart(2, '0');
                return visitYear === year && visitMonth === monthNum;
            });
        }

        if (searchQuery) {
            const query = searchQuery.toLowerCase().trim();
            visitsWithOrgNames = visitsWithOrgNames.filter(visit =>
                visit.organizationName?.toLowerCase().includes(query) ||
                visit.visitPurpose?.toLowerCase().includes(query) ||
                visit.userComment?.toLowerCase().includes(query)
            );
        }

        const total = (month || searchQuery)
            ? visitsWithOrgNames.length
            : visitsResponse.total;

        if (searchQuery || month) {
            visitsWithOrgNames = visitsWithOrgNames.slice(offset, offset + limit);
        }

        return { documents: visitsWithOrgNames, total };
    } catch (error: any) {
        console.error('Error fetching visits:', error);
        throw new Error(error.message || 'Error al cargar visitas');
    }
};

export const getGlobalStats = async (month?: string): Promise<StatsResponse> => {
    try {
        let baseQueries: string[] = [Query.limit(1)];

        if (month) {
            baseQueries = [Query.limit(500), Query.select(['status', 'visitDates', 'userId'])];
        }

        const allVisitsResponse = await databases.listDocuments(DB_ID, VISITS_COLLECTION_ID, baseQueries);
        let allVisits = allVisitsResponse.documents;

        if (month) {
            const [year, monthNum] = month.split('-');
            allVisits = allVisits.filter((visit: any) => {
                if (!visit.visitDates || visit.visitDates.length < 2) return false;
                const visitDate = new Date(visit.visitDates[1]);
                const visitYear = visitDate.getFullYear().toString();
                const visitMonth = String(visitDate.getMonth() + 1).padStart(2, '0');
                return visitYear === year && visitMonth === monthNum;
            });
        }

        const pending = allVisits.filter((v: any) => v.status === 'pending').length;
        const completed = allVisits.filter((v: any) => v.status === 'completed').length;
        const canceled = allVisits.filter((v: any) => v.status === 'canceled').length;
        const totalRecords = allVisits.length;
        const uniqueUsers = new Set(allVisits.map((v: any) => v.userId));

        return { totalRecords, totalUsers: uniqueUsers.size, pending, completed, canceled };
    } catch (error: any) {
        console.error('Error fetching global stats:', error);
        return { totalRecords: 0, totalUsers: 0, pending: 0, completed: 0, canceled: 0 };
    }
};

export const getUserStats = async (userId: string, month?: string): Promise<UserStatsResponse> => {
    try {
        const baseQueries: string[] = [
            Query.equal('userId', userId),
            Query.select(['status', 'visitDates']),
            Query.limit(500)
        ];

        const allVisitsResponse = await databases.listDocuments(DB_ID, VISITS_COLLECTION_ID, baseQueries);
        let allVisits = allVisitsResponse.documents;

        if (month) {
            const [year, monthNum] = month.split('-');
            allVisits = allVisits.filter((visit: any) => {
                if (!visit.visitDates || visit.visitDates.length < 2) return false;
                const visitDate = new Date(visit.visitDates[1]);
                const visitYear = visitDate.getFullYear().toString();
                const visitMonth = String(visitDate.getMonth() + 1).padStart(2, '0');
                return visitYear === year && visitMonth === monthNum;
            });
        }

        const pending = allVisits.filter((v: any) => v.status === 'pending').length;
        const completed = allVisits.filter((v: any) => v.status === 'completed').length;
        const canceled = allVisits.filter((v: any) => v.status === 'canceled').length;
        const totalRecords = allVisits.length;

        return { totalRecords, pending, completed, canceled };
    } catch (error: any) {
        console.error('Error fetching user stats:', error);
        return { totalRecords: 0, pending: 0, completed: 0, canceled: 0 };
    }
};

export const getUsersWithVisits = async (): Promise<string[]> => {
    try {
        const response = await databases.listDocuments(DB_ID, VISITS_COLLECTION_ID, [
            Query.select(['userId']),
            Query.limit(500)
        ]);

        return [...new Set(response.documents.map((v: any) => v.userId))];
    } catch (error: any) {
        console.error('Error fetching users with visits:', error);
        return [];
    }
};

export const getVisitById = async (visitId: string): Promise<VisitWithOrgName> => {
    try {
        const visit = await databases.getDocument<VisitDocument>(
            DB_ID,
            VISITS_COLLECTION_ID,
            visitId
        );

        const org = await databases.getDocument<OrganizationDocument>(
            DB_ID,
            ORGS_COLLECTION_ID,
            visit.organizationId
        );

        return {
            ...visit,
            organizationName: org.name,
            organizationCity: org.city,
            organizationAddress: org.address
        };
    } catch (error: any) {
        console.error('Error fetching visit:', error);
        throw new Error(error.message || 'Error al cargar la visita');
    }
};

export const updateVisit = async (
    visitId: string,
    data: {
        status?: string;
        visitPurpose?: string | null;
        branch?: string | null;
        visitDates?: string[];
        usersId?: string[] | null;
    }
): Promise<void> => {
    try {
        await databases.updateDocument(DB_ID, VISITS_COLLECTION_ID, visitId, data);
    } catch (error: any) {
        console.error('Error updating visit:', error);
        throw new Error(error.message || 'Error al actualizar la visita');
    }
};

export const AdminVisitsService = {
    getAllVisits,
    getGlobalStats,
    getUserStats,
    getUsersWithVisits,
    getVisitById,
    updateVisit,
};